import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { trackEvent } from '../lib/analytics';
import { CURRICULUM } from './curriculum';

export function TrainingPublic() {
  useEffect(() => {
    trackEvent('training_tab_opened', { from: 'public_page' });
  }, []);

  const module1 = CURRICULUM[0];

  return (
    <div className="tr-root">
      <div className="tr-wrap">
        <div className="tr-hero">
          <h1>Would your team spot it?</h1>
          <p>
            Eight modules on how scams actually work — written for people who
            don't work in security, and never assume you do.
          </p>
          <p className="tr-fine">
            Work through them at your own pace. The first module is free, no
            card required.
          </p>
        </div>

        <div className="tr-panel">
          <div className="tr-panel-head">
            <span className="tr-dot"></span>
            <b>Start with Module 1</b>
            <span>— {module1.durationLabel}, free with an account</span>
          </div>
          <div className="tr-panel-cols">
            <div className="tr-panel-col">
              <h3>What's in Module 1</h3>
              <ul className="tr-mini">
                {module1.lessons.map((lesson, i) => (
                  <li key={lesson.id}>
                    <span className="tr-lnum">{i + 1}</span>
                    {lesson.title}
                    <em>{lesson.durationMin} min</em>
                  </li>
                ))}
              </ul>
            </div>
            <div className="tr-panel-col">
              <h3>What changes after it</h3>
              <ul className="tr-outs">
                <li>
                  <i className="ti ti-check"></i>
                  You can name what a sender wants from you before you've
                  finished reading the message.
                </li>
                <li>
                  <i className="ti ti-check"></i>
                  You know the one check that stops most payment fraud, and
                  when it's worth doing.
                </li>
                <li>
                  <i className="ti ti-check"></i>
                  You stop treating "this looks normal" as evidence that it's
                  safe.
                </li>
              </ul>
            </div>
          </div>
          <div className="tr-panel-foot">
            <p>
              Module 1 is the complete module, not a preview. You'll finish
              it whether or not you ever pay us anything.
            </p>
            <Link
              className="tr-btn tr-btn-primary"
              to="/training/catalog"
              onClick={() => trackEvent('upgrade_cta_clicked', { source: 'public_hero' })}
            >
              Create a free account <i className="ti ti-arrow-right"></i>
            </Link>
          </div>
        </div>
      </div>

      <div className="tr-wrap tr-sec">
        <div className="tr-sec-head">
          <h2>The full curriculum</h2>
          <p>
            Every module is self-paced — short written lessons, real cases,
            and a knowledge check at the end. They're ordered deliberately:
            the first one teaches the pattern that every module after it
            reinforces.
          </p>
        </div>
        <div className="tr-curric">
          {CURRICULUM.map((m) => (
            <div className="tr-crow" key={m.id}>
              <span className="tr-cnum">{String(m.number).padStart(2, '0')}</span>
              <div>
                <h3>{m.title}</h3>
                <div className="tr-cmeta">
                  {m.lessons.length} lessons · {m.durationLabel}
                </div>
              </div>
              {m.access === 'free' ? (
                <span className="tr-badge tr-badge-free">Free</span>
              ) : (
                <span className="tr-badge tr-badge-pro">
                  <i className="ti ti-lock"></i>Pro
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="tr-band">
        <div className="tr-wrap">
          <span className="tr-band-eyebrow">For team managers</span>
          <h2>You're responsible for people who were never trained for this</h2>
          <p className="tr-lede">
            Your team's contact details are public. They talk to strangers
            about money all day. When one of them wires $180,000 to the
            wrong account, the first question isn't what happened — it's
            what you had in place beforehand.
          </p>
          <div className="tr-band-grid">
            <div className="tr-band-card">
              <h3>Assign it</h3>
              <p>
                Send modules to the whole team or to one person, with a due
                date. They get a link, not a login problem.
              </p>
            </div>
            <div className="tr-band-card">
              <h3>See who's actually done it</h3>
              <p>
                Module-level completion for every person on your team, so
                you know who's covered and who to chase.
              </p>
            </div>
            <div className="tr-band-card">
              <h3>Show your work</h3>
              <p>
                Exportable completion records with dates. The thing you need
                when someone asks what you did about this.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="tr-wrap tr-sec">
        <div className="tr-sec-head">
          <h2>Two ways in</h2>
          <p>Anyone can do the first module for free. The full curriculum is bought once, for a team.</p>
        </div>
        <div className="tr-plans">
          <div className="tr-plan">
            <div className="tr-plan-tag"></div>
            <h3>Free</h3>
            <p className="tr-lede">For one person who wants to know what they're missing.</p>
            <ul>
              <li><i className="ti ti-check"></i>Module 1, complete</li>
              <li><i className="ti ti-check"></i>Knowledge check and your progress</li>
              <li><i className="ti ti-check"></i>Scam Checker and fraud alerts</li>
            </ul>
            <Link className="tr-btn tr-btn-ghost" to="/training/catalog">Create a free account</Link>
          </div>
          <div className="tr-plan tr-hot">
            <div className="tr-plan-tag">Bought by managers</div>
            <h3>Pro</h3>
            <p className="tr-lede">The full curriculum for everyone who works for you, plus the tools to prove they did it.</p>
            <ul>
              <li><i className="ti ti-check"></i>All eight modules, for every person on the team</li>
              <li><i className="ti ti-check"></i>New modules as they're released</li>
              <li><i className="ti ti-check"></i>Assign modules with due dates</li>
              <li><i className="ti ti-check"></i>Team completion dashboard</li>
              <li><i className="ti ti-check"></i>Exportable completion records</li>
            </ul>
            <Link
              className="tr-btn tr-btn-primary"
              to="/training/plans"
              onClick={() => trackEvent('upgrade_cta_clicked', { source: 'public_plans' })}
            >
              Talk to us about Pro <i className="ti ti-arrow-right"></i>
            </Link>
            <p className="tr-after">Priced per person on the team. Tell us how many and we'll size it.</p>
          </div>
        </div>
      </div>

      <div className="tr-foot-note">
        First module free, no card required. Everything else is bought once, for your whole team.
      </div>
    </div>
  );
}
